import type { Event } from '@bugsnag/js';
import Bugsnag from '@bugsnag/js';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);

        // Log to Bugsnag
        Bugsnag.notify(error, (event: Event) => {
            event.addMetadata('react', { errorInfo });
        });
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            // Check if it's a known "Third Party" error like NotFoundError
            const isNotFoundError =
                this.state.error?.name === 'NotFoundError' ||
                this.state.error?.message?.includes('insertBefore') ||
                this.state.error?.message?.includes('removeChild');

            return (
                <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
                    <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-rose-50 text-rose-500 ring-8 ring-rose-50">
                        <AlertTriangle className="h-10 w-10" />
                    </div>
                    <h2 className="mb-2 text-xl font-black text-[#003628]">
                        Terjadi Gangguan Tampilan
                    </h2>
                    <p className="mb-8 max-w-xs text-sm font-medium text-[#306554]">
                        {isNotFoundError
                            ? 'Aplikasi terganggu oleh ekstensi browser atau penerjemah otomatis. Silakan segarkan halaman.'
                            : 'Terjadi kesalahan sistem saat memuat halaman ini.'}
                    </p>
                    <button
                        onClick={this.handleReset}
                        className="flex items-center gap-2 rounded-full bg-[#006944] px-8 py-4 font-bold text-white shadow-lg transition-transform active:scale-95"
                    >
                        <RefreshCw className="h-5 w-5" />
                        Segarkan Halaman
                    </button>

                    {import.meta.env.DEV && (
                        <pre className="mt-8 overflow-auto rounded-lg bg-gray-100 p-4 text-left text-[10px] text-rose-600">
                            {this.state.error?.stack}
                        </pre>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
